/* eslint-disable react/no-multi-comp */
import React from 'react';
import { useParams } from 'react-router';
import { styled } from '@mui/material/styles';
import { Box, List } from '@mui/material';
import { useResponsive } from 'src/hooks/useResponsive';
import { useRouteScenario } from 'src/hooks/useRouteScenario';
import { useLocationHelpers } from 'components/location/hooks/useLocationHelpers';
import { NotWorkingHour } from 'components/location/NotWorkingHour';
import { OrderingDisabled } from 'components/location/OrderingDisabled';
import { IOrderReadResourceV18, OrderScenario } from 'components/order/model/Order';
import { RecentOrdersList } from 'components/recentOrders/RecentOrdersList';
import { useSuggestionsContext } from '../../hooks/useSuggestionsContext';
import { RefsMap } from './Desktop/MenuDesktop';
import {
    IEnrichedCategory,
    IEnrichedMenu,
    IEnrichedMenuWithModifierMaps,
    IEnrichedProduct,
    isProductGroup,
    ProductGroup
} from './model/Menu';
import { useSuggestionModal } from './suggestions/useAddModal';
import { MenuCategory } from './MenuCategory';
import { MenuProductInfo } from './MenuProductInfo';
import { isDefined } from 'lib/typeInference';
import { getCompoundKey } from 'lib/helpers';
import { ApplicationState } from 'store/store';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { updateBasketItemQuantity, updateTimePadding } from 'store/basket/basketActions';
import { useCheckins } from 'components/user/hooks/useCheckins';
import { useDeliveryState } from 'components/delivery/hooks/useDeliveryState';

const PREFIX = 'MenuList';

const classes = {
    menu: `${PREFIX}menu`
};

const StyledBox = styled(Box, {
    shouldForwardProp: prop => prop !== 'isDesktop'
})<{ isDesktop: boolean }>(({ theme, isDesktop }) => ({
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    flex: 1,
    width: '100%',
    position: 'relative',
    ...(isDesktop
        ? {
              paddingBottom: 'calc(var(--vh, 1vh) * 100)'
          }
        : {}),

    [`& .${classes.menu}`]: {
        flex: 1,
        padding: theme.spacing(0),
        paddingBottom: theme.spacing(7)
    }
}));

interface MenuListProps {
    categories: IEnrichedMenu['categories'];
    onClick?: (product: IEnrichedProduct | ProductGroup) => void;
    currency: string;
    menu?: IEnrichedMenuWithModifierMaps;
    recentOrders?: IOrderReadResourceV18[];
    hasCategoryGroups: boolean;
    showNotWorkingHour: boolean;
    isCollapsible?: boolean;
    refsMap?: RefsMap;
    setActiveCategory?: React.Dispatch<React.SetStateAction<string | undefined>>;
}

export const MenuList: React.FC<React.PropsWithChildren<MenuListProps>> = ({
    categories,
    currency,
    menu,
    recentOrders,
    hasCategoryGroups,
    showNotWorkingHour,
    isCollapsible = true,
    setActiveCategory,
    refsMap
}) => {
    const dispatch = useDispatch();
    const { getState } = useStore<ApplicationState>();
    const { isDesktop } = useResponsive();
    const [selectedProduct, setSelectedProduct] = React.useState(
        null as null | IEnrichedProduct | ProductGroup
    );
    const [selectedCategoryId, setSelectedCategoryId] = React.useState<string>();
    const [productDetailsOpen, setProductDetailsOpen] = React.useState(false);
    const [scenario] = useRouteScenario();

    const { isLocationOrderEnabled } = useLocationHelpers();
    const context = useSuggestionsContext();
    const { suggestionModal } = useSuggestionModal();
    const basket = useSelector((state: ApplicationState) => state.basket);
    const { locationId } = useParams<{ locationId: string }>();
    const { triggerCheckin, checkinOnOrderLocationSelected } = useCheckins();
    const { selectedAddress } = useDeliveryState(false);
    const handleProductSelected = React.useCallback(
        (product: IEnrichedProduct | ProductGroup, categoryId: string) => {
            setSelectedCategoryId(categoryId);
            setSelectedProduct(product);
            setProductDetailsOpen(true);
        },
        []
    );

    const handleRemoveProduct = React.useCallback(
        (product: IEnrichedProduct | ProductGroup) => {
            if (isProductGroup(product)) {
                const groupProduct = product;
                const productIdsInBasket =
                    groupProduct?.products.length > 0
                        ? groupProduct.products
                              .filter(
                                  product =>
                                      basket.items.find(item => item.productId === product.id) !== undefined
                              )
                              .map(product => product.id)
                        : [];
                if (productIdsInBasket.length > 0) {
                    const itemRefIds: string[] = [];
                    productIdsInBasket.forEach(productId => {
                        const itemRefIdsForProduct = basket.items
                            .filter(item => item.productId === productId)
                            .map(item => item.referenceId)
                            .filter(isDefined);
                        itemRefIds.push(...itemRefIdsForProduct);
                    });
                    if (itemRefIds.length > 0) {
                        itemRefIds.forEach(itemRefId => {
                            if (isDefined(itemRefId)) {
                                updateBasketItemQuantity(itemRefId, 0)(dispatch);
                            }
                        });
                    }
                }
            } else {
                const productId = product.id;
                const itemRefIds = basket.items
                    .filter(item => item.productId === productId)
                    .map(item => item.referenceId);
                if (itemRefIds.length > 0) {
                    itemRefIds.forEach(itemRefId => {
                        if (isDefined(itemRefId)) {
                            updateBasketItemQuantity(itemRefId, 0)(dispatch);
                        }
                    });
                }
            }
            updateTimePadding()(dispatch, getState);
        },
        [basket.items, dispatch, getState]
    );

    const renderRecentOrders = React.useCallback(() => {
        if (recentOrders) {
            if (refsMap) {
                refsMap['recent-orders'] = { ref: React.createRef<HTMLDivElement>(), active: false };
                return (
                    <div ref={refsMap['recent-orders'].ref}>
                        <Box paddingX={1} paddingTop={1.5}>
                            <RecentOrdersList
                                setActiveCategory={setActiveCategory}
                                isCollapsible={!isDesktop}
                                orders={recentOrders}
                            />
                        </Box>
                    </div>
                );
            }

            return <RecentOrdersList orders={recentOrders} />;
        }
        return null;
    }, [isDesktop, recentOrders, refsMap, setActiveCategory]);
    const renderCategories = React.useCallback(
        (item: IEnrichedCategory, index: number, iteratedCategories: IEnrichedCategory[]) => {
            const isExpandedInitially = iteratedCategories.length === 1 && index === 0;
            if (refsMap) {
                const key = getCompoundKey(item.groupCategoryId, item.id);
                refsMap[key] = {
                    ref: React.createRef<HTMLDivElement>(),
                    active: false
                };

                return (
                    <div ref={refsMap[key].ref} key={`${item.id}^${item.groupCategoryId}`}>
                        <Box paddingX={1}>
                            <MenuCategory
                                isCollapsible={isCollapsible}
                                item={item}
                                onProductSelect={handleProductSelected}
                                setActiveCategory={setActiveCategory}
                                isExpandedInitially={isExpandedInitially}
                                menu={menu}
                                onRemove={handleRemoveProduct}
                            />
                        </Box>
                    </div>
                );
            }
            return (
                <MenuCategory
                    isCollapsible={isCollapsible}
                    item={item}
                    key={item.id}
                    onProductSelect={handleProductSelected}
                    isExpandedInitially={isExpandedInitially}
                    menu={menu}
                    onRemove={handleRemoveProduct}
                />
            );
        },
        [refsMap, isCollapsible, handleProductSelected, menu, setActiveCategory, handleRemoveProduct]
    );

    const handleProductDetailsClose = React.useCallback(() => {
        setProductDetailsOpen(false);
        setSelectedProduct(null);
        setSelectedCategoryId(undefined);
        context.setShowMoreSuggestionInfo(false);
        context.setShowMoreSuggestionInfoType(null);
    }, [context]);

    React.useEffect(() => {
        if (context.suggestedGroupProduct) {
            setSelectedProduct(context.suggestedGroupProduct);
            let categoryId;
            // Can't get category id from product group, so we need to get it from one of the products in the group
            if (
                'products' in context.suggestedGroupProduct &&
                Array.isArray(context.suggestedGroupProduct.products)
            ) {
                const [firstProduct] = context.suggestedGroupProduct.products;
                categoryId = menu?.productIdToCategoryId.get(firstProduct?.id);
            } else {
                categoryId = menu?.productIdToCategoryId.get(context.suggestedGroupProduct?.id);
            }
            setSelectedCategoryId(categoryId);
            setProductDetailsOpen(true);
        }
    }, [context.suggestedGroupProduct, menu?.productIdToCategoryId]);

    React.useEffect(() => {
        if (checkinOnOrderLocationSelected && locationId) {
            if (scenario !== OrderScenario.DELIVERY) {
                triggerCheckin(locationId);
                return;
            }
            if (selectedAddress) {
                triggerCheckin(locationId);
            }
        }
    }, [checkinOnOrderLocationSelected, triggerCheckin, locationId, scenario, selectedAddress]);

    return (
        <StyledBox isDesktop={isDesktop} id="menu-container">
            {showNotWorkingHour && !hasCategoryGroups && scenario && <NotWorkingHour scenario={scenario} />}
            {!isLocationOrderEnabled && (!hasCategoryGroups || isDesktop) && <OrderingDisabled />}

            <List className={classes.menu}>
                {(isDesktop || (!isDesktop && !hasCategoryGroups)) && renderRecentOrders()}
                {categories.map(renderCategories)}
            </List>
            {suggestionModal()}
            {selectedProduct && isDefined(selectedCategoryId) && (
                <MenuProductInfo
                    categoryId={selectedCategoryId}
                    item={selectedProduct}
                    currency={currency}
                    open={productDetailsOpen}
                    onClose={handleProductDetailsClose}
                />
            )}
        </StyledBox>
    );
};
